import type { TenantConfig, ThemeColors } from './tenant-config.interface.js';

/**
 * Type-Guard für `TenantConfig`.
 *
 * Prüft an der Frontend-Boot-Grenze, dass die per Nginx ausgelieferte
 * `config.json` strukturell vollständig ist. Inhaltliche Schlüssel-Validierung
 * (SPKI-Parsing, Modulus-Größe) erfolgt erst im Pinning-Gate (DEV-006);
 * dieser Guard sichert nur die Form.
 *
 * Pflicht-Bedingungen:
 * - `tenantId`, `logoUrl`, `phoneNumber`: nicht-leere Strings.
 * - `tenantPublicKeys`: non-empty Array von Strings (Key-Rotation kann zwei
 *   Einträge gleichzeitig führen, ein leeres Array wäre nicht betriebsfähig).
 * - `themeColors.primary`: Pflicht; übrige Felder optional, aber wenn vorhanden
 *   müssen sie Strings sein.
 *
 * @param candidate Unbekannte Eingabe (z. B. `await fetch('/api/config.json').then(...)`).
 */
export function isTenantConfig(candidate: unknown): candidate is TenantConfig {
  if (typeof candidate !== 'object' || candidate === null) {
    return false;
  }
  const record = candidate as Record<string, unknown>;
  return (
    isNonEmptyString(record.tenantId) &&
    isNonEmptyString(record.logoUrl) &&
    isNonEmptyString(record.phoneNumber) &&
    isPublicKeyArray(record.tenantPublicKeys) &&
    isThemeColors(record.themeColors) &&
    isOptionalLocations(record.locations) &&
    isOptionalNonEmptyString(record.tenantName) &&
    isOptionalNonEmptyString(record.imprintUrl)
  );
}

function isOptionalNonEmptyString(value: unknown): value is string | undefined {
  return value === undefined || (typeof value === 'string' && value.length > 0);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isPublicKeyArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.length > 0 && value.every(isNonEmptyString);
}

function isThemeColors(value: unknown): value is ThemeColors {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.primary)) {
    return false;
  }
  return (
    isOptionalString(record.secondary) &&
    isOptionalString(record.background) &&
    isOptionalString(record.text) &&
    isOptionalString(record.textOnPrimary)
  );
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalLocations(
  value: unknown,
): value is Readonly<Record<string, string>> | undefined {
  if (value === undefined) {
    return true;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).every(isNonEmptyString);
}
